import { useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { shiftYmd } from '../utils/ordersDailyPresentation';

/** One free-text depot / driver note, as digested by the backend. */
export interface OrderEvidenceNote {
  occurredAt?: string;
  text: string;
  noteType?: string;
  statusCode?: string;
  rawRef?: string;
  /** >1 when the digest folded in near-identical repeat scans (server-side). */
  repeatCount?: number;
}

export interface OrderEvidence {
  orderId: string;
  resolvedFrom?: string;
  evidence: {
    ordersDelivered: number;
    externalSnapshots: number;
    operatorNotes: OrderEvidenceNote[];
    events: {
      delivered: Array<Record<string, unknown>>;
      stops: Array<Record<string, unknown>>;
    };
  };
}

/** Journey windows run long — a stalled drop can take days of failed runs. */
const LOOKBACK_DAYS = 21;
const LOOKAHEAD_DAYS = 14;

/**
 * Fetch the source evidence + operator note stream for one order, from the
 * reconstruction endpoint. `enabled` gates it so the call only fires when a
 * drill-down row is actually expanded — never on render of the collapsed table.
 * `ref` may be a Palletways tracking id; the backend resolves it.
 */
export function useOrderEvidence(ref: string | null, grainDate: string | undefined, enabled: boolean) {
  const [data, setData] = useState<OrderEvidence | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled || !ref || !grainDate) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);

    const from = shiftYmd(grainDate.slice(0, 10), -LOOKBACK_DAYS);
    const to = shiftYmd(grainDate.slice(0, 10), LOOKAHEAD_DAYS);
    const url = `${API_ENDPOINTS.reconstruction.orderEvidence(ref)}?from=${from}&to=${to}`;

    void authenticatedFetch(url, { signal: ac.signal })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error || `Evidence request failed (${res.status})`);
        }
        return res.json() as Promise<OrderEvidence>;
      })
      .then((json) => { if (!ac.signal.aborted) setData(json); })
      .catch((err) => {
        if (ac.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        setError(err instanceof Error ? err.message : 'Evidence request failed');
      })
      .finally(() => { if (!ac.signal.aborted) setLoading(false); });

    return () => ac.abort();
  }, [ref, grainDate, enabled]);

  return { data, loading, error };
}
